import { memo, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { range } from 'ramda';

import { IKnmSubject } from 'APIPackage/KnmModule/interfaces/IKnmSubject';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { KNM_PREVIOUS, ROUTE_KNM } from 'CorePackage/MainModule/routes/routes';
import { Spinner } from 'UIPackage/MainModule/components/Spinner';
import { Button } from 'UIPackage/MainModule/components/Button';
import { formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { IKnmSubject248 } from 'APIPackage/KnmModule/interfaces/IKnmSubject248';
import { IPreviousKnm } from 'APIPackage/KnmModule/interfaces/IPreviousKnm';
import { Section } from 'UIPackage/FormModule/components/Section';

import { usePreviousKnms } from '../hooks/usePreviousKnms';
import styles from './KnmPrevious.module.scss';

const currentYear = new Date().getFullYear();
const years = range(2015, currentYear + 1).reverse();

interface IKnmPreviousProps {
  dataInn?: IKnmSubject['inn'];
  isNewKnm: boolean;
  dataOgrn?: IKnmSubject['ogrn'];
  organizations?: IKnmSubject248[] | null;
}

export const KnmPrevious = memo(({ dataInn, isNewKnm, dataOgrn, organizations }: IKnmPreviousProps) => {
  const [didVisible, setDidVisible] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);

  const inn = useMemo(() => {
    if (dataInn) {
      return [dataInn];
    }
    if (organizations?.length) {
      return organizations?.filter(item => item.inn).map(item => item.inn) as string[];
    }
  }, [dataInn, organizations]);

  const ogrn: string[] | undefined = useMemo(() => {
    let res: string[] | undefined;

    if (dataOgrn) {
      res = [dataOgrn];
    }
    if (organizations?.length) {
      res = organizations?.reduce((arr: string[], val) => {
        if (val?.ogrn) {
          return [...arr, val.ogrn];
        }

        return arr;
      }, []);
    }

    return res?.length ? res : undefined;
  }, [dataOgrn, organizations]);

  const updateDidVisible = useCallback(() => {
    const blockOffsetTop = blockRef.current?.getBoundingClientRect().top;

    if (blockOffsetTop && blockOffsetTop - document.documentElement.clientHeight < 0) {
      setDidVisible(true);
    }
  }, []);

  useEffect(() => {
    updateDidVisible();
    window.addEventListener('scroll', updateDidVisible);

    return () => window.removeEventListener('scroll', updateDidVisible);
  }, [updateDidVisible]);

  const [activeYear, setActiveYear] = useState(years[0]);
  const {
    isActivePreviousKnmsLoading,
    getActivePreviousKnmsNextPage,
    activePreviousKnms,
    page,
    pageCount,
  } = usePreviousKnms({
    isOn: didVisible,
    year: activeYear,
    inn,
    ogrn,
  });

  const handlerYearClick = (year: number) => () => setActiveYear(year);
  const nextPageAvailable = (page || page === 0) && (pageCount || pageCount === 0) && page + 1 < pageCount;
  const formatDocOrder = (previousKnm: IPreviousKnm): string => {
    let res = '';

    if (previousKnm.approveDocOrderNum && previousKnm.approveDocOrderDate) {
      res = `${previousKnm.approveDocOrderNum} ${previousKnm.approveDocOrderDate}`;
    } else if (previousKnm.approveDocRequestNum && previousKnm.approveDocRequestDate) {
      res = `${previousKnm.approveDocRequestNum} ${previousKnm.approveDocRequestDate}`;
    }

    return res;
  };

  const contentAttributes = {
    ...(isActivePreviousKnmsLoading && { 'data-loading': true }),
  };

  return (
    <Section title="Предыдущие КНМ" id={KNM_PREVIOUS}>
      <div ref={blockRef} />
      {!isNewKnm && (
        <div className={styles.Content} {...contentAttributes}>
          <div className={styles.Years}>
            {years.map(year => {
              const yearAttributes = {
                ...(year === activeYear && { 'data-active': true }),
              };

              return (
                <div key={year} className={styles.Year} onClick={handlerYearClick(year)} {...yearAttributes}>
                  {year}
                </div>
              );
            })}
          </div>

          {isActivePreviousKnmsLoading && <Spinner className={styles.Loader} />}

          {activePreviousKnms.length > 0 && (
            <div className={styles.PreviousKnmTableContainer}>
              <table className={styles.PreviousKnmTable}>
                <tbody className={styles.PreviousKnmTableBody}>
                  {activePreviousKnms.map(previousKnm => (
                    <tr className={styles.PreviousKnmRow} key={previousKnm.id}>
                      <td className={styles.PreviousKnmCell}>
                        <a
                          className={styles.KnmLink}
                          href={ROUTE_KNM(previousKnm.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {previousKnm.erpId}
                        </a>
                      </td>
                      <td className={styles.PreviousKnmCell}>{previousKnm.knmType}</td>
                      <td className={styles.PreviousKnmCell}>{previousKnm.kind}</td>
                      <td className={styles.PreviousKnmCell}>{formatDocOrder(previousKnm)}</td>
                      <td className={styles.PreviousKnmCell}>{previousKnm.supervisionType}</td>
                      <td className={styles.PreviousKnmCell}>
                        {`${
                          previousKnm.startDate
                            ? formatDateLocale({ date: previousKnm.startDate, dateFormat: DATE_FORMAT_LONG_FNS })
                            : '?'
                        } - ${
                          previousKnm.stopDate
                            ? formatDateLocale({ date: previousKnm.stopDate, dateFormat: DATE_FORMAT_LONG_FNS })
                            : '?'
                        }`}
                      </td>
                      <td className={styles.PreviousKnmCell}>{previousKnm.prosecutorOrganization}</td>
                      <td className={styles.PreviousKnmCell}>{previousKnm.controllingOrganization}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {nextPageAvailable && (
                <Button type="button" text="Показать еще" color="load" onClick={getActivePreviousKnmsNextPage} />
              )}
            </div>
          )}
        </div>
      )}
    </Section>
  );
});
